import { ReactNode } from "react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import s from "./Chip.module.scss";

export type TriState = "off" | "in" | "out";
export type TriStateLabels = { off: string; in: string; out: string; outPrefix: string };

type Base = { tone?: "default" | "attention"; icon?: ReactNode };
export type ChipProps = Base & (
  | { variant: "remove"; label: ReactNode; onRemove?: () => void; removeLabel: string }
  | { variant: "toggle"; label: ReactNode; pressed?: boolean; onToggle?: () => void }
  // One choice of a chip radio group (ToggleChips single): the group owns the arrow keys and the one tab stop.
  | { variant: "radio"; label: ReactNode; checked?: boolean; onSelect?: () => void; tabIndex?: number }
  | ({ variant: "tri"; state?: TriState; onCycle?: (next: TriState) => void; stateLabels: TriStateLabels }
      // Plain text for the accessible name: required when the label is not a string.
      & ({ label: string; name?: string } | { label: ReactNode; name: string }))
);

const NEXT: Record<TriState, TriState> = { off: "in", in: "out", out: "off" };
const join = (...c: (string | false | undefined)[]) => c.filter(Boolean).join(" ");

/** Filter chip: removable, on/off, one of a radio group, or three-state (off, included, left out). State is always carried by words/icon, not colour alone. */
export const Chip = (props: ChipProps) => {
  const attention = props.tone === "attention" && s.attention;
  const icon = props.icon && <span aria-hidden className={s.icon}>{props.icon}</span>;

  if (props.variant === "remove") {
    return (
      <span className={join(s.chip, s.remove, attention)}>
        {icon}<span>{props.label}</span>
        <button type="button" className={join(s.x, s.tap)} aria-label={props.removeLabel} title={props.removeLabel} onClick={props.onRemove}>
          <CloseRoundedIcon aria-hidden />
        </button>
      </span>
    );
  }

  if (props.variant === "toggle") {
    const on = !!props.pressed;
    return (
      <button type="button" aria-pressed={on} className={join(s.chip, s.tap, on && s.on, attention)} onClick={props.onToggle}>
        {on ? <span aria-hidden className={s.icon}><CheckRoundedIcon /></span> : icon}
        <span>{props.label}</span>
      </button>
    );
  }

  if (props.variant === "radio") {
    const on = !!props.checked;
    return (
      <button type="button" role="radio" aria-checked={on} tabIndex={props.tabIndex} className={join(s.chip, s.tap, on && s.on, attention)} onClick={props.onSelect}>
        {on ? <span aria-hidden className={s.icon}><CheckRoundedIcon /></span> : icon}
        <span>{props.label}</span>
      </button>
    );
  }

  const state = props.state ?? "off";
  const text = props.name ?? (typeof props.label === "string" ? props.label : "");
  // The name starts with the visible words (WCAG 2.5.3 label in name).
  const accessibleName = state === "out" ? `${props.stateLabels.outPrefix} ${text}, ${props.stateLabels.out}` : `${text}: ${props.stateLabels[state]}`;
  return (
    <button type="button" className={join(s.chip, s.tap, state === "in" && s.in, state === "out" && s.out, attention)}
      aria-label={accessibleName}
      onClick={() => props.onCycle?.(NEXT[state])}>
      {state === "in" ? <span aria-hidden className={s.icon}><CheckRoundedIcon /></span> : icon}
      <span>{state === "out" ? <>{props.stateLabels.outPrefix} {props.label}</> : props.label}</span>
    </button>
  );
};
